import React, { useState, useEffect, useMemo } from "react";
import { useCopyValue } from "hooks/useCopies";
import type { TextProps } from "@chakra-ui/react";
import { Text } from "@chakra-ui/react";

interface TypingTextProps extends TextProps {
  texts: string[];
  speed?: number;
  loop?: boolean;
  minLoopDelay?: number;
  maxLoopDelay?: number;
}

export const TypingText: React.FC<TypingTextProps> = ({
  texts,
  speed = 100,
  loop = false,
  minLoopDelay: minLoopDelayProp,
  maxLoopDelay: maxLoopDelayProp,
  ...rest
}) => {
  const defaultDelays = useCopyValue("copy_typing_text_loop_delays");

  const { minDelay, maxDelay } = useMemo(() => {
    const min = minLoopDelayProp ?? (defaultDelays?.[0] ? parseInt(defaultDelays[0], 10) : 1500);
    const max = maxLoopDelayProp ?? (defaultDelays?.[1] ? parseInt(defaultDelays[1], 10) : 3000);
    return { minDelay: min, maxDelay: max };
  }, [minLoopDelayProp, maxLoopDelayProp, defaultDelays]);

  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [typingIndex, setTypingIndex] = useState(0);

  // Effect to reset displayed text and typing index when the current text or texts array changes
  useEffect(() => {
    if (!texts || texts.length === 0) return;
    setDisplayedText("");
    setTypingIndex(0);
  }, [currentTextIndex, texts]);

  // Effect for typing the current text
  useEffect(() => {
    if (!texts || texts.length === 0) return;
    const currentText = texts[currentTextIndex];
    // Ensure currentText is valid before trying to access its properties
    if (currentText && typingIndex < currentText.length) {
      const timeoutId = setTimeout(() => {
        setDisplayedText((prev) => prev + currentText[typingIndex]);
        setTypingIndex((prev) => prev + 1);
      }, speed);
      return () => clearTimeout(timeoutId);
    }
  }, [typingIndex, currentTextIndex, texts, speed]);

  // Effect for handling the loop after the current text is fully typed
  useEffect(() => {
    if (!texts || texts.length === 0) return;
    const currentText = texts[currentTextIndex];
    // Ensure currentText is valid and fully typed before looping
    if (currentText && typingIndex === currentText.length && loop) {
      const delay = Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;
      const timeoutId = setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % texts.length);
        // Resetting typingIndex and displayedText is handled by the first useEffect
        // when currentTextIndex changes.
      }, delay);
      return () => clearTimeout(timeoutId);
    }
  }, [typingIndex, currentTextIndex, texts, loop, minDelay, maxDelay, speed]);

  // Guard for empty or invalid texts array, moved after hooks
  if (!texts || texts.length === 0) {
    return <Text {...rest}></Text>;
  }

  const currentTextForCursor = texts[currentTextIndex] || ""; // Fallback for cursor

  return (
    <Text {...rest}>
      {displayedText}
      {/* Optional: Add a blinking cursor */}
      {typingIndex < currentTextForCursor.length && <span>|</span>}
    </Text>
  );
};
